import { useState, useEffect, useMemo, useRef } from 'react'
import { useRouter } from 'next/router'
import PDModal from '../pd-modal'
import { useTranslations } from 'next-intl'
import { Form, Formik, Field, useFormikContext, FormikHelpers } from 'formik'
import PdImage from '../pd-image/pd-image'
import * as Yup from 'yup'
import clsx from 'clsx'
import { submitCommonHubspot } from '@/service/form.service'
import toast, { Toaster } from 'react-hot-toast'
import { EventReportManager } from '@/event-report/event-report-manager'
import { sha256 } from '@/common/misc'

interface FormValues {
  email: string
  agree: boolean
}

const EmailForm = () => {
  const router = useRouter()
  const t = useTranslations()
  const { errors, isSubmitting, setSubmitting, values } = useFormikContext<FormValues>()
  const [showPrivacyBounce, setShowPrivacyBounce] = useState(false)

  const emailError = useMemo(() => {
    return (errors as Record<string, any>)['email']
  }, [errors])

  useEffect(() => {
    if (isSubmitting && !values.agree) {
      setShowPrivacyBounce(true)
      setSubmitting(false)
    }
    const timer = setTimeout(() => {
      setShowPrivacyBounce(false)
    }, 1200)
    return () => clearTimeout(timer)
  }, [isSubmitting, errors])

  useEffect(() => {
    if (values.agree) {
      const event = { data: { event: 'popup_consent_checked' } }
      EventReportManager.getInstance().fireEventImmediately(event, true)
    }
  }, [values.agree])

  return (
    <Form className="flex flex-col w-full">
      <label htmlFor="email" className="flex items-center">
        <span className="text-black/90 text-base font-semibold">{t('Modal.Email')}</span>
        <img src="/icons/icon_required.svg" />
      </label>
      <div className="flex mt-2 mb-6">
        <Field
          type="text"
          name="email"
          className="h-[52px] bg-[#f2f3f5] rounded-tl rounded-bl border-none flex-1"
          placeholder={t('Modal.EmailPlaceholder')}
        />
        <button
          disabled={emailError}
          type="submit"
          className={clsx(
            'w-[72.80px] h-[52px] rounded-tr rounded-br flex justify-center items-center',
            emailError || isSubmitting ? 'bg-BBC3CE' : 'bg-pdblue'
          )}
        >
          <img src="/icons/mail.svg" width={28} height={28} alt="" />
        </button>
      </div>
      <div
        className={clsx(
          'flex text-black/60 text-sm font-normalleading-snug',
          showPrivacyBounce ? 'animate-bounce-x' : ''
        )}
      >
        <Field name="agree" type="checkbox" className="form-check-input mr-3" />
        <span>
          {t('Modal.PrivacyPrefix')}
          <a
            className="text-pdblue ml-1 hover:underline"
            target={'_blank'}
            rel="noreferrer"
            href={'/' + router.locale + '/privacy'}
          >
            {t('Modal.Privacy')}
          </a>{' '}
          {t('Modal.PrivacySuffix')}
        </span>
      </div>
    </Form>
  )
}

const LeavePromptModal = () => {
  const [showModal, setShowModal] = useState(false)
  const hasTriggeredRef = useRef(false)
  const t = useTranslations()

  const schema = Yup.object().shape({
    email: Yup.string()
      .trim()
      .required(t('FormError.Required'))
      .matches(/.*?@(?!temporary\-mail.net)/gi, 'Please fill in the correct email address'),
  })

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      // 当鼠标Y坐标小于10像素时（接近浏览器顶部）
      if (event.clientY < 10 && !hasTriggeredRef.current) {
        setShowModal(true)
        hasTriggeredRef.current = true
        const event = { data: { event: 'popup_view' } }
        EventReportManager.getInstance().fireEventImmediately(event, true)
      }
    }
    document.addEventListener('mousemove', handleMouseMove)
    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
    }
  }, [])

  const onSubmit = (values: FormValues, { setSubmitting }: FormikHelpers<FormValues>) => {
    if (!values['agree']) {
      return
    }
    submitCommonHubspot({ data: { website_subscribe_email: values.email }, email: values.email })
      .then(async (res) => {
        if (res.code === 200 || res.code === 0) {
          const sha256Email = await sha256(values.email)
          const evt = {
            data: {
              event: 'popup_submit',
              email: values.email,
              sha256_email: sha256Email,
            },
          }
          EventReportManager.getInstance().fireEventImmediately(evt, true)

          toast.success(t('Modal.Success'), {
            style: {
              backgroundColor: '#dcfce7',
            },
          })
          toast.success(t('Modal.SubmitSuccess'), {
            style: {
              backgroundColor: '#dcfce7',
            },
          })
          setShowModal(false)
        } else {
          toast.error(t('Modal.Failed'))
        }
      })
      .catch((res) => {
        const response = res.response?.data
        const error = response?.message
        if (error) {
          toast.error(error)
        } else {
          toast.error(t('Modal.Failed'))
        }
      })
      .finally(() => {
        setSubmitting(false)
      })
  }

  const onClose = () => {
    setShowModal(false)
    hasTriggeredRef.current = false
    const event = { data: { event: 'popup_close' } }
    EventReportManager.getInstance().fireEventImmediately(event, true)
  }

  return (
    <PDModal show={showModal} onClose={onClose}>
      <Toaster />
      <div className="w-full flex flex-col gap-y-6">
        <h1 className="text-black/90 text-2xl font-bold">{t('Modal.Title')}</h1>
        <PdImage src="https://cdn.pudutech.com/official-website/d9/banner_family.webp" width="100%" height="156px" />
        <p className="text-black/90 text-sm font-normal leading-snug">{t('Modal.Subscribe')}</p>
      </div>
      <Formik initialValues={{ email: '', agree: false }} validationSchema={schema} onSubmit={onSubmit} validateOnMount>
        <EmailForm />
      </Formik>
    </PDModal>
  )
}
export default LeavePromptModal
